import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeftOutline, CircledCheckIcon, MapPinIcon, PhoneIcon, TrifoldMapIcon } from "../../../icons/";
import usePersonStore from "../../../store/personStore";
import { addToCalendar, formatBookingCalendar, validateResponse } from "../deliveryUtils";
import { toast } from "../../../components/ui/Toast/useToast";
import { useOnlinePaymentInitit } from "../../../hooks/react-query/usePayment";
import React, { useEffect, useState } from "react";
import { ServiceSummarySkeletion } from "../../../components/elements/skeletions/skeletions";
import { ConfirmationDialog, CustomModal } from "../../../components/ui";
import { useDeliveryFlowStore } from "../../../store/newVehicleDeliveryStore";
import { getVehicleDeliveryCancelFn } from "../../../transactions/deliveryBooking/deliveryBooking";
import { useGetDeliveryStatus } from "../../../hooks/react-query/useVehicleDelivery";
import { queryClient } from "../../../utils/queryClient";
import { QUERY_KEYS } from "../../../config/constants";

export const BookingSummary = ({ afterViva, paymentStatus }) => {
  // Step five: Here the user sees a summary of his booked appointment

  //GLOBAL STATE:
  const summaryObject = useDeliveryFlowStore((s) => s.summaryObject);
  const carData = useDeliveryFlowStore((s) => s.carData);
  const reset = useDeliveryFlowStore((s) => s.reset);

  const carPlateno = carData?.vehPlate ?? carData?.plateno

  const { VEHICLE_DELIVERY_INFO } = QUERY_KEYS;
  
  // LOCAL STATE:
  const [isPickup, setIsPickup] = useState(false)
  const [isCancelLoading, setIsCancelLoading] = useState(false)
  const [openModal, setOpenModal] = useState(false);

  // FETCH DATA:

  // LOCAL VARIABLES:
  const navigate = useNavigate();
  const { flowId } = useParams();
  const { mutate: paymentInit } = useOnlinePaymentInitit();

  const resolvedFlowId = React.useMemo(
    () => flowId ?? summaryObject?.id,
    [flowId, summaryObject?.id]
  );

  const showRetailDashboard = usePersonStore(
    (store) => store.showRetailDashboard
  );

  const { data: deliveryInfo } = useGetDeliveryStatus({plateno: carPlateno, enabled: true})

  // USE EFFECTS:

  // This useEffect is for determining the type of the appointment (CRM form or Pickup) based on the optionSelection, which will affect the displayed information and available actions in the summary page
  useEffect(()=> {
    setIsPickup(summaryObject?.optionSelection === "HomeDelivery" ? true : false)
  }, [summaryObject?.optionSelection])
  
  // This useEffect is for showing a toast message after returning from Viva, based on the payment status
  const hasShownVivaToast = React.useRef(false);

  useEffect(() => {
    if (!afterViva || hasShownVivaToast.current) return;

    hasShownVivaToast.current = true;

    if (paymentStatus === 'success') {
      toast({
        variant: 'success',
        description: 'Η πληρωμή έγινε επιτυχώς',
      });
    } else if (paymentStatus === 'fail') {
      toast({
        variant: 'destructive',
        description: 'Η πληρωμή σου απέτυχε.',
      });
    } else {
      toast({
        variant: 'destructive',
        description: 'Παρουσιάστηκε κάποιο πρόβλημα',
      });
    }

    navigate(window.location.pathname, { replace: true });
  }, [afterViva, navigate, paymentStatus]);


  // HANDLERS & FUNCTIONS

  const vivaRepaymentHandler = () => {
    try {
      paymentInit(
        {
          serviceFlowId: resolvedFlowId,
          payment_type: 'pickupDelivery',
        },
        {
          onSuccess: (response) => {
            if (response.exceptions) {
              if (response.exceptions.length === 0) {
                const data = JSON.parse(response.data);
                const url = data.url;
                url && window.location.replace(url);
              }
            }
          },
        }
      );
    } catch(e) {
      console.warn(e)
    }
  }

  const redirectToGoogleMaps = () => {
    if(summaryObject?.optionSelection === "HomeDelivery") {
      const googleMapsUrl = `https://maps.google.com/?q=${summaryObject?.deliveryAddress}`
      window.open(googleMapsUrl, "_blank", "noopener,noreferrer");
    } else {
      const googleMapsUrl = `https://www.google.com/maps?q=${deliveryInfo?.data?.deliveryLocation?.coordinates?.lat},${deliveryInfo?.data?.deliveryLocation?.coordinates?.lng}`
      window.open(deliveryInfo?.data?.deliveryLocation?.googleMapsURL ?? googleMapsUrl, "_blank", "noopener,noreferrer");
    }
  }

  const cancelText = () => {
    return summaryObject?.optionSelection ==='HomeDelivery' ? "Θέλεις σίγουρα να ακυρώσεις το ραντεβού; Για να το προγραμματίσεις εκ νέου, επικοινώνησε άμεσα με το Τμήμα Παράδοσης Αυτοκινήτων μέσω e-mail στο car-delivery@avis.gr." : `Θέλεις να ακυρώσεις το ραντεβού παραλαβής για την 
      ${new Date(summaryObject?.bookingCalendar).toLocaleDateString('el-GR', {
          weekday: 'long',
          day: 'numeric',
          month: 'long',
          timeZone: 'UTC',
        })};`
  }

  const handleCancelAppointment = async () => {
    try {
      setIsCancelLoading(true)
      const response = await getVehicleDeliveryCancelFn({vehicleFlowId:resolvedFlowId, payload:{}});
      if (validateResponse(response)) {
       toast({
          variant: 'success',
          description: `Το ραντεβού ακυρώθηκε.${summaryObject?.optionSelection === 'HomeDelivery' ? ' Για νέο προγραμματισμό, επικοινώνησε στο car-delivery@avis.gr.' : ""}`,
        });
        queryClient.invalidateQueries({
            queryKey: [VEHICLE_DELIVERY_INFO, carPlateno],
        });
        setOpenModal(false)
        setIsCancelLoading(false)
        navigate("/dashboard");
        reset()
      }
    } catch (e) {
      console.warn('Undo failed', e);
    } finally {
      setOpenModal(false)
      setIsCancelLoading(false)
    }
  }

  const summaryTitle = () => {
    if(afterViva && paymentStatus === 'fail') {
      return (
      <>
        <h3>Το ραντεβού σου ΔΕΝ προγραμματίστηκε ακόμα...</h3>
        <p className='pt-3 text-sm text-center'>{`Η πληρωμή δεν έγινε επιτυχώς.`}</p>
        {summaryObject?.pendingPayment && 
          <p className='pt-3 text-sm text-center'>Ξαναπροσπάθησε την πληρωμή σου ή κάλεσε στο 210 6879800 για να επικοινωνήσεις με έναν εκπρόσωπό μας.</p> 
        }
      </>
      )
    } else if((afterViva && paymentStatus === 'success') || !afterViva) {
      const optionsSelection = isPickup ? "Pickup" : "Avis"

      switch (optionsSelection) {
        case "Pickup":
          return (
            <>
              <CircledCheckIcon className="w-12 h-12 mb-3" />
              <h3 className='text-center'>Το ραντεβού σου προγραμματίστηκε!</h3>
              <p className='pt-3 text-sm text-center'>Θα λάβεις όλες τις λεπτομέρειες του ραντεβού σου μέσω e-mail και sms.</p> 
              <p className='text-sm text-center'>Για την ακριβή ώρα παραλαβής, θα σε καλέσει ο οδηγός την ίδια ημέρα.</p> 
            </>
          )
        case "Avis":
          return (
            <>
              <CircledCheckIcon className="w-12 h-12 mb-3" />
              <h3 className='text-center'>Το ραντεβού σου προγραμματίστηκε!</h3>
              <p className='pt-3 text-sm text-center'>Θα λάβεις όλες τις λεπτομέρειες του ραντεβού σου μέσω e-mail και sms.</p> 
            </>
          )
        default:
          return null
      }
    }
  }

  const summaryButtons = () => {
    return (
      <div className='flex flex-row gap-4 items-center justify-center pt-6 '>
        <button className='border border-fadedgrey rounded-md cursor-pointer' onClick={()=>{
          navigate(showRetailDashboard ? "/dashboard" : "/portal/my-cars");
        }}>
          <div className='flex flex-row gap-2 items-center justify-center py-3 px-10'>
            <ArrowLeftOutline className='w-4 h-4 stroke-black' />
            <p className='text-sm font-semibold'>My Cars</p>
          </div>
        </button>
        {(!afterViva || (afterViva && paymentStatus === 'success')) &&  
        <button className='bg-[#D4002A] rounded-md cursor-pointer' onClick={()=>{
          addToCalendar(summaryObject)
        }}>
          <p className='text-sm text-white font-semibold py-3 px-10'>Προσθήκη στο ημερολόγιο</p>
        </button>}
        {summaryObject?.pendingPayment && 
          <button className='bg-[#D4002A] rounded-md cursor-pointer' onClick={vivaRepaymentHandler}>
            <div className='flex flex-row gap-2 items-center justify-center py-3 px-10'>
              <p className='text-sm text-white font-semibold'>Πληρωμή</p>
            </div>
          </button>
        }
      </div>
    )
  }

  const timeInfo = () => {
    const {day,time} = formatBookingCalendar(summaryObject?.bookingCalendar)
    return (
      <>
        <p className="text-sm font-bold mb-2 pt-6">{"Επιλεγμένη ημέρα & ώρα"}</p>
        <p className="text-sm mb-2">{day}</p>
        <p className="text-sm">{time}</p>
      </>
    )
  }

  return (
    <>
      {!summaryObject ? <ServiceSummarySkeletion /> : <div className="flex flex-col w-full items-center justify-center mt-16" style={{ minHeight: "calc(100vh / 2)"}}>
        {summaryTitle()}
        {summaryButtons()}
        <div className='min-w-full md:min-w-[568px] min-h-[263px] m-8 p-6 border border-[#999999] rounded-xl bg-white'>
          <p className="text-sm font-bold mb-2 pt-6">Διεύθυνση παραλαβής</p>
          <div className='flex flex-row gap-2 items-center '>
            <MapPinIcon className='w-4 h-4'/>
            <p className="text-sm">{summaryObject?.optionSelection === "HomeDelivery" ? summaryObject?.deliveryAddress : deliveryInfo?.data?.deliveryLocation?.address }</p>
          </div>
          {summaryObject?.phones?.length > 0 && <div className='flex flex-row gap-2 items-center'>
            <PhoneIcon className='w-4 h-4'/>
            <p className="text-sm">{summaryObject?.phones?.[0]}</p>
          </div>}
          {(deliveryInfo?.data?.deliveryLocation?.coordinates) && <button 
            disabled={!isPickup && !deliveryInfo?.data?.deliveryLocation?.coordinates} 
            className='bg-none flex flex-row gap-2 items-center mt-4 px-10 py-3 border rounded-lg border-fadedgrey' 
            onClick={redirectToGoogleMaps}>
              <TrifoldMapIcon className={`w-4 h-4 ${(!isPickup && !deliveryInfo?.data?.deliveryLocation?.coordinates) ? "text-fadedgrey" : "text-black"}`} />
              <p className={`text-sm ${(!isPickup && !deliveryInfo?.data?.deliveryLocation?.coordinates) ? "text-fadedgrey" : "text-black"} font-semibold`}>Δες το στον χάρτη</p>
          </button>}
          {timeInfo()}
          {isPickup && 
          <>
            <p className="text-sm mt-6 font-bold">Home Delivery</p>
            <p className="text-sm pt-2">Ναι</p>
          </>}
          {<CustomModal
            triggerComponent={
              <p className="text-sm font-semibold text-[#D4002A] cursor-pointer pt-6">Ακύρωση ραντεβού</p>
            }
            openModal={openModal}
            handleClick={(value) => {setOpenModal(value)}}
          >
            <div className="container-sm mx-auto py-10">
              <ConfirmationDialog
                message={cancelText()}
                title="Ακύρωση προγραμματισμένου ραντεβού"
                isLoading = {isCancelLoading}
                handleOkClick={()=>handleCancelAppointment()}
                handleCancelClick={() => setOpenModal(false)}
                confirmCtaText="Ακύρωση"
                cancelCtaText="Επιστροφή"
              />
            </div>
          </CustomModal>}
        </div>
      </div>}
    </>
  );
};
